import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useDiagramDocument } from '../../../contexts/DiagramDocumentContext';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { useScenariosStore } from '../../../core/assistant/stores/scenariosStore';
import { useDiagramFork } from './useDiagramFork';
import {
  successionPlanningKeys,
  useSuccessionPlanningProjection,
} from './useSuccessionPlanningProjection';
import {
  replaySuccessionSlates,
  successionCarryPlanFromProjection,
} from '../services/successionSlateCarry';
import type {
  SuccessionPlanningProjection,
  SuccessionSlateFact,
} from '../models/SuccessionPlanningPersistence';
import type { MasterChartPlanSeed } from '../components/useMasterChartChangeIntercept';
import { publishSuccessionCarryComplete } from '../services/successionCarrySignal';
import { createTransientPlanningScenario } from '../services/planningScenarioSeed';
import { useOrgPlanningScenarioStore } from '../stores/orgPlanningScenarioStore';

function openPendingTab(): Window | null {
  if (typeof window === 'undefined') return null;
  const pending = window.open('about:blank', '_blank');
  if (pending) pending.opener = null;
  return pending;
}

function mergeSuccessionSlates(
  current: readonly SuccessionSlateFact[],
  seeded: readonly SuccessionSlateFact[],
): SuccessionSlateFact[] {
  const byTarget = new Map(current.map((slate) => [slate.targetRef, slate]));
  for (const slate of seeded) byTarget.set(slate.targetRef, slate);
  return Array.from(byTarget.values());
}

/**
 * "Save as": put the active scenario's changes into their own chart.
 *
 * - 'plan' (default) — a linked chart: the changes arrive as a live scenario
 *   with the record bindings intact, so they can still be applied to the org
 *   chart from there.
 * - 'copy' — a frozen, disconnected snapshot that never syncs back.
 *
 * A pending succession draft is carried across too. Succession lives in a
 * separate, diagram-scoped server change-set that the fork cannot copy, so its
 * slates are replayed into the new chart's draft. If the actor's permissioned
 * view of the succession plan is incomplete, the carry is skipped and said
 * out loud rather than writing a quietly lossy copy of restricted data.
 */
export function useScenarioSaveAs() {
  const { doc, diagramId } = useDiagramDocument();
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const nodes = useDiagramStore((s) => s.nodes);
  const edges = useDiagramStore((s) => s.edges);
  const { data: diagramInfo } = useDiagramInfo(diagramId ?? undefined);
  const getActiveScenario = useScenariosStore((s) => s.getActiveScenario);
  const folderId = diagramInfo?.folderId ?? '';
  const { createFork, isLoading } = useDiagramFork({
    diagramId: diagramId ?? '',
    diagramName: diagramInfo?.name ?? 'Untitled',
    folderId,
    ydoc: doc,
    nodes,
    edges,
  });
  const succession = useSuccessionPlanningProjection({
    folderId,
    diagramId,
    nodes,
    edges,
    enabled: Boolean(folderId && diagramId),
  });

  /** Replay the pending succession slates into the newly created chart. */
  const carrySuccession = useCallback(async (
    targetDiagramId: string,
    seededSlates: readonly SuccessionSlateFact[] = [],
    signal?: AbortSignal,
    sourceScenarioCarried = false,
  ): Promise<void> => {
    if (signal?.aborted) return;
    const selected = diagramId
      ? useOrgPlanningScenarioStore.getState().selectedByDiagram[diagramId]
      : undefined;
    const acceptedProjection = selected && diagramId
      ? queryClient.getQueryData<SuccessionPlanningProjection>(successionPlanningKeys.projection(
        folderId,
        diagramId,
        selected.scenarioId,
        selected.headChangeSetId,
      ))
      : undefined;
    const plan = successionCarryPlanFromProjection(sourceScenarioCarried ? null : acceptedProjection ?? succession.query.data);
    if (!plan.complete) {
      toast.warning('Succession changes stayed on this chart', {
        description: 'Some succession details are outside your access, so copying them would leave an incomplete plan.',
      });
      return;
    }
    const slates = mergeSuccessionSlates(plan.slates, seededSlates);
    if (slates.length === 0) return;
    try {
      const headsResponse = await authenticatedFetch(
        `/api/folders/${folderId}/diagrams/${targetDiagramId}/atlas/organization-plans`, { signal },
      );
      if (!headsResponse.ok) throw new Error('Could not resolve the target plan before carrying succession');
      const heads = await headsResponse.json() as Array<{ scenarioId: string; headChangeSetId: string }>;
      if (heads.length > 1) throw new Error('Select the target draft before carrying succession');
      const { carriedCount } = await replaySuccessionSlates({
        slates,
        initialHead: heads[0] ? { scenarioId: heads[0].scenarioId, changeSetId: heads[0].headChangeSetId } : undefined,
        putSlate: async (body: {
          changeSetId?: string;
          scenarioId?: string;
          expectedHeadChangeSetId?: string;
          slate: SuccessionSlateFact;
        }) => {
          if (signal?.aborted) throw signal.reason;
          const response = await authenticatedFetch(
            `/api/folders/${folderId}/diagrams/${targetDiagramId}/atlas/succession/change-set`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
              signal,
            },
          );
          if (!response.ok) {
            const failed = await response.json().catch(() => null) as { error?: string } | null;
            throw new Error(failed?.error ?? `Failed to carry succession (${response.status})`);
          }
          return response.json() as Promise<{ changeSetId: string; scenarioId: string }>;
        },
      });
      toast.success(`Carried ${carriedCount} succession change${carriedCount === 1 ? '' : 's'} across`);
    } catch (error) {
      if (signal?.aborted) return;
      toast.warning('Succession changes stayed on this chart', {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }, [authenticatedFetch, diagramId, folderId, queryClient, succession.query.data]);

  const saveAs = useCallback(async (
    mode: 'plan' | 'copy' = 'plan',
    planSeed?: MasterChartPlanSeed,
    signal?: AbortSignal,
  ) => {
    if (signal?.aborted) return;
    const scenario = planSeed?.scenario
      ?? getActiveScenario()
      ?? (planSeed ? createTransientPlanningScenario() : null);
    if (!scenario || isLoading) return;
    // Popup creation must happen in the original click gesture. Waiting for
    // the fork and protected-draft replay first makes browsers treat the tab as
    // an unsolicited popup and block it.
    const pendingTab = openPendingTab();
    try {
      const result = await createFork(scenario, mode);
      if (signal?.aborted) {
        pendingTab?.close();
        return;
      }
      if (!result.success || !result.redirectUrl) {
        pendingTab?.close();
        toast.error('Could not save', { description: result.error });
        return;
      }
      // Carry the active view mode onto the draft. The redirect the server
      // builds has no query string, so without this the succession-planning
      // lens (or whatever `?review=` the gesture happened in) never activates
      // in the new tab — the draft opens in the default view.
      let redirectUrl = result.redirectUrl;
      if (mode === 'plan') {
        const review = planSeed?.reviewId
          ?? new URLSearchParams(window.location.search).get('review');
        if (review) {
          const target = new URL(result.redirectUrl, window.location.origin);
          target.searchParams.set('review', review);
          redirectUrl = `${target.pathname}${target.search}${target.hash}`;
        }
      }
      // The planning artifact is complete and safe to view at this point.
      // Navigate now; protected succession replay can take tens of seconds and
      // must not leave the user staring at about:blank while it runs.
      if (pendingTab) pendingTab.location.href = redirectUrl;
      // A frozen copy is deliberately disconnected, so it carries no live draft.
      if (mode === 'plan' && result.newDiagramId) {
        await carrySuccession(result.newDiagramId, planSeed?.successionSlates, signal, result.carriedSourceScenario);
        if (signal?.aborted) return;
        publishSuccessionCarryComplete(folderId, result.newDiagramId);
      }
      toast.success(mode === 'plan' ? 'Saved as a new chart' : 'Exported a copy');
      // An async confirmation/command queue can outlive popup activation. The
      // artifact and protected carry have settled: open the exact returned URL
      // in this tab instead of stranding it behind another blocked popup.
      if (!pendingTab) window.location.assign(redirectUrl);
    } catch (error) {
      pendingTab?.close();
      toast.error('Could not save', {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }, [carrySuccession, createFork, folderId, getActiveScenario, isLoading]);

  return { saveAs, isSaving: isLoading };
}
